import { Fragment } from 'react';

import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  type RenderDropdownItemsParams,
} from '@air/primitive-dropdown-menu';
import { cn } from '@air/utils-tailwind';

import { AgentDropdownMenuSub } from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/AgentDropdownMenuSub';
import {
  agentDropdownMenuItem,
  agentDropdownMenuLabel,
  agentDropdownMenuSeparator,
} from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/agentDropdownMenuVariants';

/**
 * Agent selection bar menu renderer — same option contract as `renderDropdownItems`, with dark-glass
 * className applied on each primitive. Keeps Radix primitives and keyboard/focus behaviour intact.
 */
export const renderAgentDropdownItems = ({ options, size = 'small' }: RenderDropdownItemsParams) => {
  if (!options) {
    return null;
  }

  return options.map((option, index) => {
    switch (option.type) {
      case 'children':
        return <Fragment key={option.id || index}>{option.children}</Fragment>;
      case 'item':
        return (
          <DropdownMenuItem
            className={cn(agentDropdownMenuItem(), option.className)}
            data-id={option.id}
            data-testid="AGENT_SELECTION_BAR_DROPDOWN_MENU_ITEM"
            description={option.description}
            disabled={option.disabled}
            key={option.id || index}
            label={option.label}
            onSelect={option.onSelect}
            prefix={option.prefix}
            shortcut={option.shortcut}
            size={size}
            suffix={option.suffix}
            truncate={option.truncate}
          />
        );
      case 'radio-group':
        return (
          <DropdownMenuRadioGroup key={option.id} onValueChange={option.onValueChange} value={option.value}>
            {renderAgentDropdownItems({
              options: option.options,
              size,
            })}
          </DropdownMenuRadioGroup>
        );
      case 'radio-item':
        return (
          <DropdownMenuRadioItem
            className={cn(agentDropdownMenuItem(), option.className)}
            data-id={option.id}
            data-testid="AGENT_SELECTION_BAR_DROPDOWN_MENU_ITEM"
            description={option.description}
            disabled={option.disabled}
            key={option.id || index}
            label={option.label}
            onSelect={option.onSelect}
            prefix={option.prefix}
            size={size}
            value={option.value}
          />
        );
      case 'separator':
        return (
          <DropdownMenuSeparator
            className={cn(agentDropdownMenuSeparator(), option.className)}
            data-id={option.id}
            data-testid="AGENT_SELECTION_BAR_DROPDOWN_MENU_SEPARATOR"
            key={option.id || index}
          />
        );
      case 'sub':
        return (
          <AgentDropdownMenuSub
            className={option.className}
            data-id={option.id}
            description={option.description}
            disabled={option.disabled}
            key={option.id || index}
            label={option.label}
            prefix={option.prefix}
            size={size}
            suffix={option.suffix}
          >
            {renderAgentDropdownItems({
              options: option.options,
              size,
            })}
          </AgentDropdownMenuSub>
        );
      case 'title':
        return (
          <DropdownMenuLabel
            className={cn(agentDropdownMenuLabel(), option.className)}
            data-id={option.id}
            data-testid="AGENT_SELECTION_BAR_DROPDOWN_MENU_LABEL"
            key={option.id || index}
            size={size}
          >
            {option.label}
          </DropdownMenuLabel>
        );
      default:
        return null;
    }
  });
};
